/**
 * The secret scan for FREE TEXT (a document's or a task's Markdown) — git
 * sync Stage 2, card S2.2. YAML is scanned by structure (secretScan.js);
 * Markdown has no structure to walk, so it is read line by line with the
 * same narrow formats the YAML scan and the AI redaction use: vendor keys,
 * JWTs, private keys, URLs with inline credentials, `Bearer <token>`, and a
 * credential-named `key: value` whose value is long and not a {{variable}}.
 * Prose that only MENTIONS a password ("the password is in the Vault") passes.
 *
 * So do PLACEHOLDERS (05.10.2026 — 4 of 4 findings on the owner's real boards
 * were docs examples: `mysql://user:pass@host`, `password: "super-secret-…"`):
 * a value made only of example words, `<…>` / `[…]`, `***`, or a `${VAR}` /
 * `$VAR` / `%VAR%` reference is not a secret — the stop-word idea of gitleaks
 * and GitGuardian. Vendor formats (keys, JWTs, tokens) are never excused.
 */
const LINE_PATTERNS = [
  [/-{5}BEGIN [A-Z ]*PRIVATE KEY-{5}/, 'a private key'],
  [/\bsk-ant-[A-Za-z0-9_-]{16,}/, 'an API key'],
  [/\bsk-[A-Za-z0-9_-]{16,}/, 'an API key'],
  [/\bgh[pousr]_[A-Za-z0-9]{16,}/, 'a GitHub token'],
  [/\bxox[baprs]-[A-Za-z0-9-]{10,}/, 'a Slack token'],
  [/\bAKIA[0-9A-Z]{16}\b/, 'an AWS access key'],
  [/\bAIza[0-9A-Za-z_-]{35}\b/, 'a Google API key'],
  [/\bey[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/, 'a JWT'],
  [/\b(Bearer|Basic|Token)\s+[A-Za-z0-9._~+/=-]{16,}/, 'an Authorization token'],
];

/** `scheme://user:password@` — every one on the line; the password is group 1. */
const URL_CREDENTIAL = /[a-z][a-z0-9+.-]*:\/\/[^/\s:@]+:([^/\s@]+)@/gi;

/** Words an example value is made of ("your-password", "p:ss", "super-secret-password"). */
const EXAMPLE_WORDS = new Set([
  'password', 'passwd', 'pass', 'pwd', 'pw', 'p', 'ss', 'secret', 'super', 'my', 'your', 'the', 'db', 'database',
  'example', 'sample', 'test', 'demo', 'dummy', 'fake', 'changeme', 'change', 'me', 'placeholder', 'strong',
  'redacted', 'foo', 'bar', 'baz', 'here', 'value', 'token', 'key', 'api', 'some', 'very', 'long', 'new', 'old',
]);

const decode = (v) => {
  try {
    return decodeURIComponent(v);
  } catch {
    return v;
  }
};

/** An obvious stand-in rather than a real credential. */
export function isPlaceholderSecret(raw) {
  const v = decode(String(raw || '').trim().replace(/^["'`]+|["'`,;]+$/g, ''));
  if (!v) return true;
  if (/^\{\{[^}]+\}\}$/.test(v)) return true;
  if (/^<[^<>]+>$|^\[[^\]]+\]$/.test(v)) return true;
  if (/^\$\{?[A-Za-z_][A-Za-z0-9_]*\}?$|^%[A-Za-z_][A-Za-z0-9_]*%$/.test(v)) return true;
  if (/^(\*+|x+|\.+|…+|•+)$/i.test(v)) return true;
  // Only letters and separators: `Password1` / `pass123` may well be a real (weak) password.
  if (/\d/.test(v)) return false;
  const words = v.toLowerCase().split(/[^a-z]+/).filter(Boolean);
  return words.length > 0 && words.every((w) => EXAMPLE_WORDS.has(w) || /^x+$/.test(w));
}

const ASSIGNMENT =
  /(?:^|[\s;,&("'])(password|passwd|pwd|secret|token|api[_-]?key|apikey|access[_-]?token|refresh[_-]?token|client[_-]?secret|auth[_-]?token|private[_-]?key)\s*[=:]\s*("[^"]*"|'[^']*'|[^\s;,&)]+)/i;

/** @returns {{ line: number, rule: string }[]} one finding per line at most */
export function scanTextForSecrets(text) {
  const findings = [];
  String(text || '').split('\n').forEach((line, i) => {
    for (const [re, what] of LINE_PATTERNS) {
      if (re.test(line)) {
        findings.push({ line: i + 1, rule: `looks like ${what}` });
        return;
      }
    }
    if ([...line.matchAll(URL_CREDENTIAL)].some((m) => !isPlaceholderSecret(m[1]))) {
      findings.push({ line: i + 1, rule: 'looks like a URL with a password in it' });
      return;
    }
    const m = ASSIGNMENT.exec(line);
    if (m) {
      const value = m[2].replace(/^["']|["']$/g, '');
      if (value.length >= 12 && !isPlaceholderSecret(value)) {
        findings.push({ line: i + 1, rule: `looks like a credential in "${m[1]}"` });
      }
    }
  });
  return findings;
}
